<script setup lang="ts">
import NeedHelp from '~/components/NeedHelp.vue';
import ScrollToTop from '~/components/ScrollToTop.vue';
import LazyImage from '~/components/LazyImage.vue';
import { useGlobalStore } from '~/stores/global';
import { STORE_TYPE } from '~/utils/constants';

const globalStore = useGlobalStore();
</script>
<template>
	<div>
		<ScrollToTop />
		<MobileHeader :show-cart="false">
			<template #title> Terms and Conditions</template>
			<template #actions>
				<div></div>
			</template>
		</MobileHeader>
		<div
			class="flex mt-[59px] w-full flex-col justify-center gap-7xl overflow-y-auto overscroll-contain bg-white py-7xl px-3xl"
		>
			<p class="text-gray-600 text-sm">
				By accessing our website, you are agreeing to be bound by these terms of
				service, and agree that you are responsible for compliance with any
				applicable local laws.
			</p>
			<!-- Need Help -->
			<NeedHelp
				v-if="globalStore.store.store_type === STORE_TYPE.MARKETPLACE"
			/>
			<div class="flex flex-col gap-3xl text-sm text-gray-600">
				<div>
					These terms and conditions (together with the documents referred to in
					it) tells you the terms and conditions on which you may make use of
					our website {{ globalStore.store.domain }} (the “site”), whether as a
					guest or a registered user. Use of our site includes accessing,
					browsing, or registering to use our site.
				</div>
				<div>
					Please read these terms and conditions carefully as these will apply
					to your use of our site. We recommend that you print a copy of this
					for future reference.
				</div>
				<div>
					By using our site, you confirm that you accept these terms and
					conditions and that you agree to comply with them.
				</div>
				<div>
					If you do not agree to these terms and conditions, you must not use
					our site.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OTHER APPLICABLE TERMS
					</div>
					These terms and conditions refer to the following additional terms,
					incorporated into the terms and conditions, which also apply to your
					use of our site:
				</div>
				<div>
					Our Privacy Policy, which sets out the terms on which we process any
					personal data we collect from you, or that you provide to us. By using
					our site, you consent to such processing and you warrant that all data
					provided by you is accurate.
				</div>
				<div>
					Our Acceptable Use Policy, which sets out the permitted uses and
					prohibited uses of our site. When using our site, you must comply with
					this Acceptable Use Policy.
				</div>
				<div>
					Our Cookie Policy, which sets out information about the cookies on our
					site.
				</div>
				<div>
					Our Refunds Policy, which sets out information about returns and
					refunds.
				</div>
				<div>
					Shipping Policy, which sell out information about Shipments rate,
					Taxes, Customs and duties as well as estimated delivery timelines.
				</div>
				<div>
					If you purchase goods from our site, our terms and conditions of
					supply (“terms of supply”) will apply to the sales.
				</div>

				<div class="flex flex-col gap-xl">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						CHANGES TO THESE TERMS
					</div>
					<div>
						We may revise these terms and conditions at any time by amending
						this page. We might revise our terms and conditions after every 6
						months.
					</div>
					<div>
						Please check this page from time to time to take notice of any
						changes we made, as they are binding on you.
					</div>
				</div>
				<div class="flex flex-col gap-xl">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						CHANGES TO OUR SITE
					</div>
					<div>
						We may update our site from time to time, and may change the content
						at any time. However, please note that any of the content on our
						site may be out of date at any given time, and we are under no
						obligation to update it.
					</div>
					<div>
						We do not guarantee that our site, or any content on it, will be
						free from errors or omissions.
					</div>
				</div>
				<div class="flex flex-col gap-xl">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						ACCESSING OUR SITE
					</div>
					<div>Our site is made available free of charge.</div>
					<div>
						We do not guarantee that our site, or any content on it, will always
						be available or be uninterrupted. Access to our site is permitted on
						a temporary basis. We may suspend, withdraw, discontinue or change
						all or any part of our site without notice. We will not be liable to
						you if for any reason our site is unavailable at any time or for any
						period.
					</div>
					<div>
						You are responsible for making all arrangements necessary for you to
						have access to our site.
					</div>
					<div>
						You are also responsible for ensuring that all persons who access
						our site through your internet connection are aware of these terms
						and conditions and other applicable terms and conditions, and that
						they comply with them.
					</div>
					<div>
						We may limit the availability of our site or any service or product
						described on our site to any person or geographic area at any time.
					</div>
				</div>
				<div
					v-if="globalStore.store?.store_type === STORE_TYPE.MARKETPLACE"
					class="flex flex-col gap-xl"
				>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						REGISTRATION, YOUR ACCOUNT AND PASSWORD
					</div>
					<div>
						To use some of the services or features made available to you on
						this site, you will need to register. When you register, you are
						required to provide information about yourself that is true,
						accurate, current and complete in all respects. Should any of your
						registration information change, please notify us immediately at
						{{ globalStore.store.meta_data?.email }} or Whatsapp us on
						<a
							:href="`${globalStore.store.meta_data?.email}`"
							target="_blank"
							style="color: #868686"
						>
							<span class="font-medium">{{
								globalStore.store.meta_data?.phone
							}}</span>
						</a>
						for assistance. We may also change registration requirements from
						time to time.
					</div>
					<div>
						If you choose, or you are provided with, a user identification code,
						password or any other piece of information as part of our security
						procedures, you must treat such information as confidential. You
						must not disclose it to any third party.
					</div>
					<div>
						We have the right to disable any user identification code or
						password, whether chosen by you or allocated by us, at any time, if
						in our reasonable opinion you have failed to comply with any of the
						provisions of these terms and conditions.
					</div>
					<div>
						If you know or suspect that anyone other than you knows your user
						identification code or password, you must promptly notify us at
						{{ globalStore.store.meta_data?.email }} or Whatsapp us on
						<a
							:href="`${globalStore.store.meta_data?.whatsapp}`"
							target="_blank"
							style="color: #868686"
						>
							<span class="font-medium">{{
								globalStore.store.meta_data?.phone
							}}</span>
						</a>
						for assistance.
					</div>
				</div>
				<div class="flex flex-col gap-xl">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						ELIGIBILITY TO PURCHASE
					</div>
					<div>
						In order to make purchases on the site, you will be required to
						provide your personal details. In particular, you must provide your
						real name, phone number, email address and other requested
						information as indicated. Furthermore, you will be required to
						provide payment details that you represent and warrant are both
						valid and correct and you confirm that you are the person referred
						to in the billing information provided. The site is available only
						to individuals and others who meet our terms of eligibility, who
						have been issued with a valid debit/credit card by a bank acceptable
						to us, whose applications are acceptable to us and who have
						authorised us to process a charge or charges on their debit/credit
						card in the amount of the total purchase price for the merchandise
						which they purchase. We reserve the right to restrict multiple
						quantities of an item being shipped to any one customer or postal
						address. By making an offer to purchase merchandise, you expressly
						authorise us to perform credit checks and where we feel necessary,
						to transmit or to obtain information (including any updated
						information) about you to or from third parties, including but not
						limited to your debit/credit card number or credit reports, to
						authenticate your identity, to validate your debit/credit card, to
						obtain an initial debit/credit card authorisation and to authorise
						individual purchase transactions. Furthermore, you agree that we may
						use personal information provided by you in order to conduct
						appropriate anti-fraud checks. Personal information that you provide
						may be disclosed to a credit reference agency or fraud prevention
						agency, which may keep a record of that information.
					</div>
					<div>
						In Case of any special condition or scenario,
						{{ globalStore.store.name ?? 'Octane' }} holds the right to
						disclose/share your personal information with the relevant
						authorities.
					</div>
				</div>
				<div class="flex flex-col gap-xl">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">ORDERS</div>
					All orders are subject to acceptance and availability, and items in
					our shopping basket are not reserved and may be purchased by other
					customers. We offer products for sale that are in stock and available
					for dispatch. Occasionally, we may be waiting for shipments from our
					designer suppliers. Consequently, you may from time to time be given
					the possibility of making an advance payment for certain items in
					which case you are able to make an advance purchase. This will ensure
					that you receive this item in priority once designer stock has been
					delivered to us. We will only take advance purchase orders for stock
					that has been scheduled for delivery by a designer supplier. Your
					rights regarding advance purchase are the same as those for any other
					purchase on our site. Alternatively, you may simply choose to register
					your email address for notification of arrival of the selected
					merchandise not held in stock. Items received into stock may be
					pre-allocated to satisfy advance payment orders and customers making
					advance payments will receive items in priority to customers on the
					waiting list or customers ordering through the site for immediate
					delivery. Please be aware that we may be unable to deliver selected
					advance payment merchandise due to production problems or quality
					checks issues identified when we receive an order into stock. In these
					circumstances we will notify you and refund the advance payment to
					your debit/credit card within thirty days of being advised that
					merchandise has become unavailable. If you have registered your email
					address for notification of the arrival of a specific product featured
					on our site, we will attempt to notify you by email when the product
					becomes available. Please be aware that on occasion certain products
					that are in particularly high demand will sell out during this time.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						PRICING POLICY
					</div>
					All prices and offers remain valid as advertised from time to time.
					Product prices are set using existing currency exchange rates. These
					prices are subject to change if the applicable currency exchange rate
					changes before your order is accepted or the items are subject to
					markdowns at any time during the selling period. The price applicable
					to your order will be the price current at the time your order is
					accepted. Once an item has been purchased at a discounted sale price,
					no price adjustment will be offered should the item be further marked
					down. Your card will be charged in currency based on the place where
					the goods are purchased. Prices are displayed to you when you enter
					the site in the currency used for the country indicated by your
					computer’s IP address. If you are a customer whose debit/credit card
					is not denominated in Pakistani Rupees (PKR) or US Dollars (USD), the
					final price will be calculated in accordance with the applicable
					exchange rate on the day your card issuer processes the transaction.
					If you are in Pakistan and choose to have the goods shipped to
					Pakistan, prices will be shown inclusive of all taxes (where
					applicable) at the current rate. All Washington state USA customers
					will be charged County/Municipal and State Tax at the time of the
					checkout. All other countries are shown a price excluding taxes and as
					the recipient, you are liable for all import duties, customs and local
					sales taxes levied by the country you are in. Payment of these at the
					time of delivery is necessary to release your order from customs on
					arrival. At the time of refunds, the exchange rate of the currency at
					the time you made a purchase will be applicable.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						ACCEPTANCE OF YOUR ORDER
					</div>
					Once you have made your choice and your order has been placed, you
					will receive an email acknowledging the details of your order. This
					email is not an acceptance of your order, just a confirmation that we
					have received it. Unless you cancel your order, acceptance of your
					order and completion of the contract between you and us will be
					completed when we email you to confirm that the goods have been
					dispatched. We reserve the right not to accept your order in the
					event, for example, that we are unable to obtain authorization for
					payment, that shipping restrictions apply to a particular item, the
					item that is ordered is out of stock or does not satisfy our quality
					control standards and is withdrawn, or that you do not meet the
					eligibility criteria set out within the terms of sale. We may also
					refuse to process and therefore accept a transaction for any reason or
					refuse service to anyone at any time at our sole discretion. We will
					not be liable to you or to any third party for the reason of our
					withdrawing any merchandise from the site whether or not that
					merchandise has been sold, removing screening or editing any materials
					or content on the site, refusing to process a transaction or unwinding
					or suspending any transaction after it has begun.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">PAYMENT</div>
					Payment can be made by Visa, Mastercard, American Express, Quickpay
					Zelle and any other methods which may be clearly advertised on the
					site from time to time. You confirm that the debit/credit card that is
					being used is yours or that you have been specifically authorised by
					the owner of the debit/credit card to use it. All debit/credit card
					holders are subject to validation checks and authorisation by the card
					issuer. If the issuer of your payment card refuses to authorise
					payment to us, we will not be liable for any delay or non-delivery. We
					take reasonable care to make our site secure. All debit/credit card
					transactions on this site are processed using a secure online payment
					gateway that encrypts your card details in a secure host environment.
					These details will be fully encrypted and only used to process card
					transactions which you have initiated. To help ensure that your
					shopping experience is safe, simple and secure, we use SSL technology.
					Furthermore, we take reasonable care, in so far as it is in our power
					to do so, to keep details of your order and payment secure, but in the
					absence of negligence on our part we cannot be held liable for any
					loss you may suffer if a third party procures unauthorised access to
					any data you provide when accessing or ordering from the site.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						INTELLECTUAL PROPERTY RIGHTS
					</div>
					<div class="flex flex-col gap-6">
						Your use of the site and its contents grants no rights to you in
						relation to any copyright, designs, trademarks and all other
						intellectual property and material relating to the Content (as
						described below), including our software and all html and other code
						contained in the site. We are the owner or the licensee of all
						intellectual property rights in our site, and in the material
						published on it. Those works are protected by copyright laws and
						treaties around the world. All such rights are reserved. You are
						permitted to use the Content only as expressly authorised by us
						and/or our third party licensors. We grant you a limited, revocable
						and non-exclusive license to access and make personal use of the
						site. You may print off one copy, and may download extracts, of any
						page(s) from our site for your personal use and you may draw the
						attention of others within your organisation to content posted on
						our site.
						<div>
							You must not modify the paper or digital copies of any materials
							you have printed off or downloaded in any way, and you must not
							use any illustrations, photographs, video or audio sequences or
							any graphics separately from any accompanying text.
						</div>
						<div>
							Our status (and that of any identified contributors) as the
							authors of content on our site must always be acknowledged. You
							must not use any part of the content on our site for commercial
							purposes without obtaining a licence to do so from us or our
							licensors. If you print off, copy or download any part of our site
							in breach of these terms and conditions, your right to use our
							site will cease immediately and you must, at our option, return or
							destroy any copies of the materials you have made.
						</div>
					</div>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">CONTENT</div>
					In addition to the intellectual property rights mentioned above,
					“Content” is defined as any graphics, photographs, including all image
					rights, sounds, music, video, audio or text on this site. We try to
					ensure that the information on this site is accurate and complete.
					Although we make reasonable efforts to update the information on our
					site, we make no representations, warranties or guarantees, whether
					expressed or implied, that the content on our site is accurate,
					complete or up-to-date. We do not promise that the functional aspects
					of our site or our Content will be error free or that this site, our
					Content or the server that makes it available are free of viruses or
					other harmful components. We always recommend that all users of the
					internet ensure they have up to date virus checking software
					installed. The personal opinions of the designers and labels whose
					products we sell, or any third parties with whom we are associated are
					their own and do not necessarily reflect our views and we accept no
					responsibility for any such views expressed in any media.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						UPLOADING CONTENT TO OUR SITE
					</div>
					Whenever you make use of a feature that allows you to upload content
					to our site, or to make contact with other users of our site, you must
					comply with the content standards set out in our Acceptable Use
					Policy. You warrant that any such contribution does comply with those
					standards, and you will be liable to us and indemnify us for any
					breach of that warranty. If you are a consumer user, this means you
					will be responsible for any loss or damage we suffer as a result of
					your breach of warranty. Any content you upload to our site will be
					considered non-confidential and non-proprietary. You retain all of
					your ownership rights in your content, but you are required to grant
					us and other users of the Site a limited licence to use, store and
					copy that content and to distribute and make it available to third
					parties. The rights you license to us are described in the next
					paragraph (Rights you license). We also have the right to disclose
					your identity to any third party who is claiming that any content
					posted or uploaded by you to our site constitutes a violation of their
					intellectual property rights, or of their right to privacy. We will
					not be responsible, or liable to any third party, for the content or
					accuracy of any content posted by you or any other user of our site.
					We have the right to remove any posting you make on our site if, in
					our opinion, your post does not comply with the content standards set
					out in our Acceptable Use Policy. The views expressed by other users
					on our site do not represent our views or values. You are solely
					responsible for securing and backing up your content.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						RIGHTS YOU LICENSE
					</div>
					When you upload or post content to our site, you grant the following
					licenses:
					<ul class="list-disc list-inside flex flex-col">
						<li>
							You grant us a perpetual, worldwide, non-exclusive, royalty-free,
							transferable licence to use, reproduce, distribute, prepare
							derivative works of, display and perform such content in
							connection with the service provided by our site and across all
							media including for the promotion of the service.
						</li>
						<li>
							You grant third parties (including but not limited to, other users
							of our site, our partners and/or advisors) the right to use such
							content in accordance with the functionality of our site.
						</li>
					</ul>
					Licences granted by you will be for as long as such content is hosted
					on our site. Should you delete such content from our site the licenses
					to use such content shall remain in force for a period of 12 months
					from deletion
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">VIRUSES</div>
					We do not guarantee that our site will be secure or free from bugs or
					viruses. You are responsible for configuring your information
					technology, computer programmes and platform in order to access our
					site. You should use your own virus protection software. You must not
					misuse our site by knowingly introducing viruses, trojans, worms,
					logic bombs or other material which is malicious or technologically
					harmful. You must not attempt to gain unauthorised access to our site,
					the server on which our site is stored or any server, computer or
					database connected to our site. You must not attack our site via a
					denial-of-service attack or a distributed denial-of service attack. By
					breaching this provision, you would commit a criminal offence under
					the Computer Misuse Act 1990. We will report any such breach to the
					relevant law enforcement authorities and we will cooperate with those
					authorities by disclosing your identity to them. In the event of such
					a breach, your right to use our site will cease immediately.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						LINKING TO OUR SITE
					</div>
					You may link to our home page, provided you do so in a way that is
					fair and legal and does not damage our reputation or take advantage of
					it. You must not establish a link in such a way as to suggest any form
					of association, approval or endorsement on our part where none exists.
					You must not establish a link to our site in any website that is not
					owned by you. Our site must not be framed on any other site, nor may
					you create a link to any part of our site other than the home page. We
					reserve the right to withdraw linking permission without notice. The
					website in which you are linking must comply in all respects with the
					content standards set out in our Acceptable Use Policy. If you wish to
					make any use of content on our site other than that set out above,
					please contact
					{{ globalStore.store.meta_data?.email }}
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						THIRD PARTY LINKS AND RESOURCES IN OUR SITE
					</div>
					Where our site contains links to other sites and resources provided by
					third parties, these links are provided for your information only. We
					have no control over and are not responsible for the content or
					accuracy of those sites or resources, nor are we responsible or liable
					for any damage, loss or offence caused or alleged to be caused by or
					in connection with the use or reliance on any such content on such
					other sites and third party resources. NO RELIANCE ON INFORMATION The
					content on our site is provided for general information only. It is
					not intended to amount to advice on which you should rely. You must
					obtain professional or specialist advice before taking, or refraining
					from, any action on the basis of the content on our site.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						NO COMMERCIAL USE
					</div>
					This site is for your personal non-commercial use only. You may not
					modify, copy, distribute, transmit, display, perform, reproduce,
					publish, license, commercially exploit, create derivative works from,
					transfer or sell any content, software, products or services contained
					within this site. You may not use this site, or any of its content, to
					further any commercial purpose, including any advertising or
					advertising revenue generation activity on your site, and we have no
					liability to you for any loss of profit, loss of business, business
					interruption, or loss of business opportunity.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						YOUR ACTIVITY
					</div>
					You agree that you will be personally responsible for your use of this
					site and for all of your communication and activity on and pursuant to
					this site. If we determine that you are or have been engaged in
					prohibited activities, were not respectful of other users, or
					otherwise violated the terms of sale, we may deny you access to the
					site on a temporary or permanent basis.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						LIMITATION OF OUR LIABILITY
					</div>
					<div class="flex flex-col gap-6">
						Nothing in these terms and conditions excludes or limits our
						liability for death or personal injury arising from our negligence,
						or our fraud or fraudulent misrepresentation, or any other liability
						that cannot be excluded or limited by English law. Nothing in the
						terms of sale is intended to affect your rights under the law. If we
						breach the terms of sale, we shall only be liable for losses that
						are direct losses and are a reasonably foreseeable consequence of
						such a breach. Losses are foreseeable where, for example, they could
						be contemplated by you and us at the time of you placing your order,
						and us dispatching your order. We shall only be liable for losses up
						to 150% of the total value of goods purchased. We are not
						responsible for losses not caused by our breach or negligence,
						indirect losses which are a side effect of the main loss or damage
						and which are not reasonably foreseeable by you or us (for example,
						loss of profits or loss of opportunity). We are also not responsible
						for failure to meet any of our obligations under the terms of sale
						where such failure is due to events beyond our reasonable control.
						<div>
							We will not be liable for any loss or damage caused by a virus,
							distributed denial-of-service attack, or other technologically
							harmful material that may infect your computer equipment, computer
							programs, data or other proprietary material due to your use of
							our site or to your downloading of any content on it, or on any
							website linked to it.
						</div>
					</div>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						COMPENSATION
					</div>
					At our request, you agree to compensate us fully, defend us and hold
					us harmless immediately on demand, our officers, directors, agents,
					affiliates, licensors and suppliers, from and against all liabilities,
					claims, expenses, damages and losses, including legal fees, arising
					from any breach of the terms of sale by you, including the use by any
					other persons accessing this site using your internet account caused
					by your action or inaction.
				</div>
				<div v-if="globalStore.storeType == STORE_TYPE.MARKETPLACE">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR RELATIONSHIP
					</div>
					You acknowledge and agree that no joint venture, partnership,
					employment, or agency relationship exists between you and us as a
					result of the terms of sale or your use of this site. You agree that
					you may not and will not hold yourself out as our representative,
					agent or employee and we shall not be liable for any representation,
					act or omission on your part.<br />
					NO WAIVER<br />
					If you breach the terms of sale and we take no action against you, we
					will still be entitled to use our rights and remedies in any other
					situation where you breach the terms of sale.<br />
					CONTACT US<br />
					To contact us, please email
					{{ globalStore.store.meta_data?.email }} or Whatsapp us on
					<a
						:href="`${globalStore.store.meta_data?.whatsapp}`"
						target="_blank"
						style="color: #868686"
					>
						<span class="font-medium">{{
							globalStore.store.meta_data?.phone
						}}</span>
					</a>
					for assistance. Thank you for visiting our site.
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						THESE TERMS
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							What these terms cover. These are the terms and conditions on
							which we supply goods to you.
						</li>
						<li>
							Why you should read them. Please read these terms carefully before
							you submit your order to us. These terms tell you who we are, how
							we will provide products to you, how you and we may change or end
							the contract, what to do if there is a problem and other important
							information.
						</li>
					</ul>
				</div>
				<div v-if="globalStore.store.store_type === STORE_TYPE.MARKETPLACE">
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						INFORMATION ABOUT US AND HOW TO CONTACT US
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Who we are. We are LAAM Technologies Inc a company registered in
							the USA as well as in Pakistan.
						</li>
						<li>
							How to contact us.To contact us, please email
							{{ globalStore.store.meta_data?.email }}
							or Whatsapp us on
							<a
								:href="`${globalStore.store.meta_data?.whatsapp}`"
								target="_blank"
								style="color: #868686"
							>
								<span class="font-medium">{{ globalStore.store.meta_data?.phone }}
								</span>
							</a>
							for assistance.
						</li>
						<li>
							How we may contact you. If we have to contact you we will do so by
							telephone or by writing to you at the email address or postal
							address you provided to us in your order.
						</li>

						<li>
							"Writing" includes emails. When we use the words "writing" or
							"written" in these terms, this includes emails.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR CONTRACT WITH YOU
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							How we will accept your order. Our acceptance of your order will
							take place when we email you to accept it, at which point a
							contract will come into existence between you and us.
						</li>
						<li>
							If we cannot accept your order. If we are unable to accept your
							order, we will inform you of this and will not charge you for the
							product.
						</li>
						<li>
							Your order number. We will assign an order number to your order
							and tell you what it is when we accept your order. It will help us
							if you can tell us the order number whenever you contact us about
							your order.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR PRODUCTS
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Products may vary slightly from their pictures. The images of the
							products on our website are for illustrative purposes only.
							Although we have made every effort to display the colours
							accurately, we cannot guarantee that a device's display of the
							colours accurately reflects the colour of the products. Your
							product may vary slightly from those images.
						</li>

						<li>
							Product packaging may vary. The packaging of the product may vary
							from that shown in images on our website.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						YOUR RIGHTS TO MAKE CHANGES
					</div>
					If you wish to make a change to the product you have ordered please
					contact us. We will let you know if the change is possible. If it is
					possible we will let you know about any changes to the price of the
					product, the timing of supply or anything else which would be
					necessary as a result of your requested change and ask you to confirm
					whether you wish to go ahead with the change. If we cannot make the
					change or the consequences of making the change are unacceptable to
					you, you may want to end the contract (see clause 8 - Your rights to
					end the contract).
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR RIGHTS TO MAKE CHANGES
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Minor changes to the products. We may change the product to
							reflect changes in relevant laws and regulatory requirements and
							to implement minor technical adjustments and improvements, for
							example to address a security threat. These changes will not
							affect your use of the product.
						</li>

						<li>
							More significant changes to the products and these terms. We may
							make the changes to these terms or the product, but if we do so we
							will notify you and you may then contact us to end the contract
							before the changes take effect and receive a refund for any
							products paid for but not received.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						PROVIDING THE PRODUCTS
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Delivery costs. The costs of delivery will be as displayed to you
							on our website.
						</li>
						<li>
							When we will provide the products. We will deliver them to you as
							soon as reasonably possible and in any event within the estimated
							delivery time indicated on the website.
						</li>
						<li>
							We are not responsible for delays outside our control. If our
							supply of the products is delayed by an event outside our control
							then we will contact you as soon as possible to let you know and
							we will take steps to minimise the effect of the delay. Provided
							we do this we will not be liable for delays caused by the event,
							but if there is a risk of substantial delay you may contact us to
							end the contract and receive a refund for any products you have
							paid for but not received.
						</li>
						<li>
							If you are not at home when the product is delivered. If no one is
							available at your address to take delivery and the products cannot
							be posted through your letterbox, we will leave you a note
							informing you of how to rearrange delivery or collect the products
							from a local depot.
						</li>
						<li>
							If you do not re-arrange delivery. If you do not collect the
							products from us as arranged or if, after a failed delivery to
							you, you do not re-arrange delivery or collect them from a
							delivery depot we will contact you for further instructions and
							may charge you for storage costs and any further delivery costs.
							If, despite our reasonable efforts, we are unable to contact you
							or re-arrange delivery or collection we may end the contract and
							clause 2 will apply.
						</li>
						<li>
							Your legal rights if we deliver goods late. You have legal rights
							if we deliver any goods late. If we miss the delivery deadline for
							any goods then you may treat the contract as at an end straight
							away if any of the following apply:
							<ul>
								<li>1. We have refused to deliver the goods;</li>
								<li>
									2. Delivery within the delivery deadline was essential (taking
									into account all the relevant circumstances); or
								</li>
								<li>
									3. You told us before we accepted your order that delivery
									within the delivery deadline was essential.
								</li>
							</ul>
						</li>
					</ul>
					When you become responsible for the goods. Products which will be your
					responsibility from the time we deliver the product to the address you
					gave us or you collect it from us.
					<ul class="list-disc list-inside flex flex-col">
						<li>
							When you own goods. You own the goods once they have been
							delivered.
						</li>
						<li>
							What will happen if you do not give required information to us. We
							may need certain information from you so that we can supply the
							products to you. If so, this will have been stated in the
							description of the products on our website. We will contact you to
							ask for this information. If you do not give us this information
							within a reasonable time of us asking for it, or if you give us
							incomplete or incorrect information, we may either end the
							contract (and clause 2 will apply). We will not be responsible for
							supplying the products late or not supplying any part of them if
							this is caused by you not giving us the information we need within
							a reasonable time of us asking for it.
						</li>
						<li>
							Reasons we may suspend the supply of products to you. We may have
							to suspend the supply of a product to:
						</li>
						<li>
							deal with technical problems or make minor technical changes;
						</li>
						<li>
							update the product to reflect changes in relevant laws and
							regulatory requirements;
						</li>
						<li>
							make changes to the product as requested by you or notified by us
							to you (see clause 6).
						</li>

						<li>
							Your rights if we suspend the supply of products. We will contact
							you in advance to tell you we will be suspending supply of the
							product, unless the problem is urgent or an emergency. You may
							contact us to end the contract for a product if we suspend it, or
							tell you we are going to suspend it and we will refund any sums
							you have paid in advance for the product in respect of the period
							after you end the contract.
						</li>
						<li>
							We may also suspend supply of the products if you do not pay. If
							you do not pay us for the products when you are supposed to (see
							clause 4) and you still do not make payment within 14 days of us
							reminding you that payment is due, we may suspend supply of the
							products until you have paid us the outstanding amounts. We will
							contact you to tell you we are suspending supply of the products.
							We will not suspend the products where you dispute the unpaid
							invoice (see clause 12.6). We will not charge you for the products
							during the period for which they are suspended. As well as
							suspending the products we can also charge you a fee on your
							overdue payments (see clause 12.5).
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						YOUR RIGHTS TO END THE CONTRACT
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							You can always end your contract with us. Your rights when you end
							the contract will depend on what you have bought, whether there is
							anything wrong with it, how we are performing and when you decide
							to end the contract:
							<ul class="list-disc list-inside flex flex-col">
								<li>
									If what you have bought is faulty or misdescribed you may have
									a legal right to end the contract (or to get the product
									repaired or replaced or a service re-performed or to get some
									or all of your money back), see clause 11;
								</li>
								<li>
									If you want to end the contract because of something we have
									done or have told you we are going to do, see clause 2;
								</li>
								<li>
									If you have just changed your mind about the product, see
									clause 3. You may be able to get a refund if you are within
									the returns period, but this may be subject to deductions and
									you will have to pay the costs of return of any goods;
								</li>
								<li>
									In all other cases (if we are not at fault and there is no
									right to change your mind), see clause 6.
								</li>
							</ul>
						</li>
						Ending the contract because of something we have done or are going
						to do. If you are ending a contract for a reason set out at (a) to
						(e) below the contract will end immediately and we will refund you
						in full for any products which have not been provided and you may
						also be entitled to compensation. The reasons are: we have told you
						about an upcoming change to the product or these terms which you do
						not agree to (see clause 2); we have told you about an error in the
						price or description of the product you have ordered and you do not
						wish to proceed; there is a risk that supply of the products may be
						significantly delayed because of events outside our control; we have
						suspended supply of the products for technical reasons, or notify
						you we are going to suspend them for technical reasons, in each case
						for a period of more than 21 days; or you have a legal right to end
						the contract because of something we have done wrong (including
						because we have delivered late (see clause 7).
					</ul>
					When you don't have the right to change your mind. You do not have a
					right to change your mind in respect of items which are made to order,
					bespoke or personalized.
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Ending the contract where we are not at fault and there is no
							right to change your mind. Even if we are not at fault and you do
							not have a right to change your mind (see clause 1), you can still
							end the contract before it is completed (on delivery), but you may
							have to pay us compensation. The contract will end immediately and
							we will refund any sums paid by you for products not provided but
							we may deduct from that refund (or, if you have not made an
							advance payment), charge you for reasonable compensation for the
							net costs we will incur as a result of your ending the contract.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						HOW TO END THE CONTRACT WITH US (INCLUDING IF YOU HAVE CHANGED YOUR
						MIND)
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Tell us you want to end the contract. To end the contract with us,
							please let us know by emailing us at
							{{ globalStore.store.meta_data?.email }}. Please provide your
							name, home address, details of the order and, where available,
							your phone number and email address.
						</li>
						<li>
							Returning products after ending the contract. If you end the
							contract for any reason after products have been dispatched to you
							or you have received them, you must return them to us. You must
							write to us at
							{{ globalStore.store.meta_data?.email }}
							and we will give you instructions for returning the products.
						</li>
						<li>
							When will we pay the costs of return? We will pay the costs of
							return:
							<ul class="list-disc list-inside flex flex-col">
								<li>if the products are faulty or misdescribed;</li>
								or
								<li>
									if you have a legal right to do so as a result of something we
									have done wrong.
								</li>
							</ul>
						</li>
					</ul>
					In all other circumstances you must pay the costs of return.
					<ul class="list-disc list-inside flex flex-col">
						<li>
							What we charge for collection. If you are responsible for the
							costs of return and we are collecting the product from you, we
							will charge you the direct cost to us of collection.
						</li>
						<li>
							How we will refund you. We will refund you the price you paid for
							the products including delivery costs, by the method you used for
							payment. However, we may make deductions from the price, as
							described below.
						</li>
						<li>
							Deductions from refunds if you are exercising your right to change
							your mind. If you are exercising your right to change your mind we
							may reduce your refund of the price (excluding delivery costs) to
							reflect any reduction in the value of the goods, if this has been
							caused by your handling them in a way which would not be permitted
							in a shop. If we refund you the price paid before we are able to
							inspect the goods and later discover you have handled them in an
							unacceptable way, you must pay us an appropriate amount. The
							maximum refund for delivery costs will be the costs of delivery by
							the least expensive delivery method we offer.
						</li>
						<li>
							When your refund will be made. We will make any refunds due to you
							as soon as possible. If you are exercising your right to change
							your mind then and we have not offered to collect them, your
							refund will be made within 15 business days from the day on which
							we receive the product back from you or, if earlier, the day on
							which you provide us with evidence that you have sent the product
							back to us. For information about how to return a product to us,
							see clause 9.2.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR RIGHTS TO END THE CONTRACT
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							We may end the contract if you break it. We may end the contract
							for a product at any time by writing to you if:
							<ul class="list-disc list-inside flex flex-col">
								<li>
									you do not make any payment to us when it is due and you still
									do not make payment within 14 days of us reminding you that
									payment is due;
								</li>
								<li>
									you do not, within a reasonable time of us asking for it,
									provide us with information that is necessary for us to
									provide the products; or
								</li>
								<li>
									you do not, within a reasonable time, allow us to deliver the
									products to you or collect them from us.
								</li>
							</ul>
						</li>
						<li>
							You must compensate us if you break the contract. If we end the
							contract in the situations set out in clause 1 we will refund any
							money you have paid in advance for products we have not provided
							but we may deduct or charge you reasonable compensation for the
							net costs we will incur as a result of your breaking the contract.
						</li>
						<li>
							We may withdraw the product. We may write to you to let you know
							that we are going to stop providing the product. We will refund
							any sums you have paid in advance for products which will not be
							provided.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						IF THERE IS A PROBLEM WITH THE PRODUCT
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							How to tell us about problems. If you have any questions or
							complaints about the product, please contact us. You can email us
							at
							{{ globalStore.store.meta_data?.email }}
						</li>
						<li>
							Your obligation to return rejected products. If you wish to
							exercise your legal rights to reject products you must return them
							to us by emailing us at
							{{ globalStore.store.meta_data?.email }}
							to arrange returns.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						PRICE AND PAYMENT
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							Where to find the price for the product. The price of the product
							will be the price indicated on the order pages when you placed
							your order. We take all reasonable care to ensure that the price
							of the product advised to you is correct. However please see
							clause 3 for what happens if we discover an error in the price of
							the product you order.
						</li>
						<li>
							What happens if we get the price wrong? It is always possible
							that, despite our best efforts, some of the products we sell may
							be incorrectly priced. We will normally check prices before
							accepting your order so that, where the product's correct price at
							your order date is less than our stated price at your order date,
							we will charge the lower amount. If the product's correct price at
							your order date is higher than the price stated to you, we will
							contact you for your instructions before we accept your order.
						</li>
						<li>
							When you must pay and how you must pay. We accept payment by
							credit or debit card, Bank transfer, direct deposit or Quickpay
							Zelle; you must pay for the products before we dispatch them.
						</li>
						<li>
							What to do if you think an invoice is wrong. If you think an
							invoice is wrong please contact us promptly to let us know. You
							will not have to pay any charges until the dispute is resolved.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OUR RESPONSIBILITY FOR LOSS OR DAMAGE SUFFERED BY YOU
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							We are responsible to you for foreseeable loss and damage caused
							by us. If we fail to comply with these terms, we are responsible
							for loss or damage you suffer that is a foreseeable result of our
							breaking this contract or our failing to use reasonable care and
							skill. Loss or damage is foreseeable if either it is obvious that
							it will happen or if, at the time the contract was made, both we
							and you knew it might happen, for example, if you discussed it
							with us during the sales process.
						</li>
						<li>
							We do not exclude or limit in any way our liability to you where
							it would be unlawful to do so. This includes liability for death
							or personal injury caused by our negligence or the negligence of
							our employees, agents or subcontractors; for fraud or fraudulent
							misrepresentation; for breach of your legal rights in relation to
							the products as summarised at clause 2; and for defective products
							under the Consumer Protection Act 1987.
						</li>
						<li>
							We are not liable for business losses. We only supply the products
							for domestic and private use. If you use the products for any
							commercial, business or re-sale purpose we will have no liability
							to you for any loss of profit, loss of business, business
							interruption, or loss of business opportunity.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						HOW WE MAY USE YOUR PERSONAL INFORMATION
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							How will we use your personal information? We will use the
							personal information you provide to us:
							<ul class="list-disc list-inside flex flex-col">
								<li>to supply the products to you;</li>
								<li>to process your payment for the products; and</li>
								<li>
									if you agreed to this during the order process, to give you
									information about similar products that we provide, but you
									may stop receiving this at any time by contacting us.
								</li>
							</ul>
						</li>
						<li>
							We will only give your personal information to third parties where
							the law either requires or allows us to do so.
						</li>
					</ul>
				</div>
				<div>
					<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
						OTHER IMPORTANT TERMS
					</div>
					<ul class="list-disc list-inside flex flex-col">
						<li>
							We may transfer this agreement to someone else. We may transfer
							our rights and obligations under these terms to another
							organisation.
						</li>
						<li>
							You need our consent to transfer your rights to someone else. You
							may only transfer your rights or your obligations under these
							terms to another person if we agree to this in writing.
						</li>

						<li>
							Nobody else has any rights under this contract. This contract is
							between you and us. No other person shall have any rights to
							enforce any of its terms. Neither of us will need to get the
							agreement of any other person in order to end the contract or make
							any changes to these terms.
						</li>
						<li>
							If a court finds part of this contract illegal, the rest will
							continue in force. Each of the paragraphs of these terms operates
							separately. If any court or relevant authority decides that any of
							them are unlawful, the remaining paragraphs will remain in full
							force and effect.
						</li>
					</ul>
					Even if we delay in enforcing this contract, we can still enforce it
					later. If we do not insist immediately that you do anything you are
					required to do under these terms, or if we delay in taking steps
					against you in respect of your breaking this contract, that will not
					mean that you do not have to do those things and it will not prevent
					us taking steps against you at a later date. For example, if you miss
					a payment and we do not chase you but we continue to provide the
					products, we can still require you to make the payment at a later
					date.
				</div>
				<div v-if="globalStore.store.store_type === STORE_TYPE.MARKETPLACE">
					<div class="my-4 text-[24px] font-semibold leading-[32px] text-dark">
						Policies:
					</div>
					<div>
						<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
							Shipping & Customs:
						</div>
						We strive to deliver products purchased from LAAM in a highly
						specialized manner along with ensuring that the outfit is sanitized,
						packed modestly, and is delivered at your doorsteps in the shortest
						time possible. LAAM observes shipping fare zone wise. Below given
						are the details:
						<div class="flex mt-4 flex-col gap-4">
							<div>
								Delivery Charges For Pakistan:<br />
								Order Value: Under PKR 3,000<br />
								Shipping Fare: 150<br />
								Order Value: Above PKR 3,001<br />
								Shipping Fare: PKR 250<br />
								Countries Included: Pakistan
							</div>
							<div>
								For other countries delivery charges kindly hit the link below
								Visit: https://{{ globalStore.store.domain }}/pages/shipping
							</div>
							Delivery Timeline: <br />Luxury Pret: 4-5 weeks<br />
							Formals: 6-8 weeks<br />
							Bridals: 8-12 weeks<br />
							Unstitched: 7 days<br />
							<div>
								*For international orders, delivery timeline may fluctuate ± 2
								weeks
								<br />
								**This is standard delivery timeline. However, based on the
								availability of slots, nature of order (normal/fast track) and
								season of order placement, the delivery time frame can vary. To
								get the exact delivery timeframe of your orders, please contact
								{{ globalStore.store.meta_data?.email }}.
							</div>
						</div>
					</div>
					<div>
						<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
							Refund Policy:
							<br />
							How to file for a Refund?
						</div>
						To apply for a Refund against an order please email us your order #
						with product name, original proof of purchase receipt (proof of
						payment) and your complaint/reason/issue for requesting a refund at
						{{ globalStore.store.meta_data?.email }} and our customer care agent
						will get in contact with you. Alternatively you can call us on
						{{ globalStore.store.meta_data?.phone }} from Monday to Saturday
						between 10:00 am - 6:00 pm (Pakistan Standard Time). Further, to
						proceed with the Refund against a purchase case, your purchase
						should be sent back to us within 14 days of receiving your order.
						Visit : https://{{
							globalStore.store.domain
						}}/pages/refund-policy?contact_posted=true#contact_form REFUND
						Procedure: In case you want to claim a refund from
						{{ globalStore.store.name }}, kindly follow below given steps:
						<ul>
							<li>
								1. Contact our customer care via WhatsApp/ Email Id or reach out
								to us on {{ globalStore.store.meta_data?.email }}.
							</li>
							<li>
								2. Provide our customer care staff with your order number and
								original order confirmation receipt sent to you via email, proof
								of payment Order Confirmations & Receipts are automatically
								generated by our system and sent out to the email ID provided by
								the customer once the customer checks out and completes the
								order purchase process on our website.
							</li>
							<li>
								3. Submit a request and a detailed reason/complaint/issue for
								requesting for a Refund claim.
							</li>
						</ul>
					</div>
					<div>
						<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
							ELIGIBILITY CRITERIA
						</div>
						<div class="flex mt-4 flex-col gap-4">
							For a Refund LAAM caters refund for following mentioned reasons,
							<ul class="list-disc list-inside flex flex-col">
								<li>
									Delayed by brand: If 14 working days have passed since your
									estimated delivery timeline provide by LAAM at time of
									purchase confirmation, given that vendor is allowed to change
									the estimated delivery timeline only once, due to any reason
									that may affect production implied by designer/brand,
									government order or an act of God, when the customer is
									pre-informed either by the LAAM’s team or designer’s team.
								</li>
								<li>
									Out of stock: If the brand/vendor notifies us that your
									preferred item is being out of stock/ out of production and
									can no longer be made available in the future.
								</li>
								<li>
									Return: In case you have filed for return and we have approved
									and received your return in any of our LAAM warehouses and not
									the designer’s then it is eligible for refund. In such cases,
									LAAM will only refund the cost of the product and not bear
									charges for courier, return-shipping, or any amendments and
									stitching done to the product by the customer. In case a
									parcel has been stuck in customs and is returned back to the
									LAAM warehouse, then shipping charges are non-refundable.
								</li>
								<li>
									Exchange: If you have exchanged/ replaced your outfit with
									another outfit available in stock on our website and there is
									a pending balance amount at our end. In such cases we will
									refund your amount or provide you with store credit as per
									your preference.
								</li>
								<li>
									Post delivery issues:
									<ul class="list-disc list-inside flex flex-col">
										<li>
											First 24 hours after delivery of the Order: Client has to
											report a complaint.
										</li>
										<li>
											Next 24 hours after reporting a complaint: Client has to
											provide proof of their refund claim in the form of
											pictures, videos or any other compelling evidence.
										</li>
										<li>
											Next 24 hours after providing the proof : Client must
											provide a return receipt once the case manager has
											approved the return.
										</li>
									</ul>
								</li>
							</ul>
							<div class="font-sembold text-dark">Please Note:</div>
							<div>
								If your order is made to order then refunds won't be catered
								after your order has gone into production i.e. After 48 hours of
								order confirmation, the made to order articles cannot be
								refunded.
							</div>
							<div>
								Some Brands/Vendors/Designers do not offer refund option on
								certain articles from their Luxury Pret/Formals/Bridals
								collections although in standard sizes due to their price point
								and therefore LAAM would not be able to refund those article.
							</div>
						</div>
					</div>
					<div>
						<div class="font-semibold text-gray-800 mt-9xl mb-3xl">
							NON ELIGIBILITY CRITERIA
						</div>
						For a Refund<br />
						Outfit is not-eligible for refund/return/exchange in following
						cases:
						<ul>
							<li>
								<div class="font-semibold">
									1. Violation of Care instructions
								</div>
								Following points will be considered as violation of care
								instructions:
								<ul class="list-disc list-inside flex flex-col">
									<li>If the outfit has been shrunk.</li>
									<li>If the outfit has been washed/ dry clean.</li>
									<li>
										If an outfit has been stitched even in cases of missing
										pieces.
									</li>
									<li>If the outfit has been worn or tried on.</li>
									<li>
										If the item has been subjected to bleach/chemical for stain
										removing.
									</li>
									<li>
										A list of care instructions are provided by the designer and
										if any of those instructions are also not followed properly
										then the order is not eligible for a refund.
									</li>
								</ul>
							</li>
							<li>
								<div class="font-semibold">
									2. Inability to provide the Proof:
								</div>
								If no proof has been provided in support of the claim of
								‘eligibility’ within the 24 hours.
							</li>
							<li>
								<div class="font-semibold">
									3. Timeline for sharing Return Receipt slip:
								</div>
								If return has been accepted and return receipt is not shared
								within the given time period of acceptance of the refund.
							</li>
							<li>
								<div class="font-semibold">4. Packaging is tampered:</div>
								Orders are not eligible for refund, return or exchange, if
								packaging is opened, damaged or tampered at the time of
								receiving by the customer.
							</li>
							<li>
								<div class="font-semibold">5. Sale Price Adjustment:</div>
								LAAM does not cater requests for price change in case a customer
								has bought an article before sale at original price and wants to
								be compensated with a refund for the difference in price after
								the sale has been implied.
							</li>
							<li>
								<div class="font-semibold">6. Change of mind:</div>
								Laam does not cater “CHANGE OF MIND/ NO LONGER NEEDED/ NO
								TECHNICAL REASON BUT JUST WANT TO CANCEL THE ORDER/ DIDN’T LIKE
								THE PRODUCT AFTER OPENING THE PACKAGE/ ORDER BY MISTAKE” as
								reason for cancellation/refund/exchange if reported after 48
								hours of placing the order.
							</li>
							<li>
								<div class="font-semibold">7. Advance Payment Clause:</div>
								Any amount paid as ‘ advance payment’ for bridal/couture orders
								is non-refundable and non-transferrable.
							</li>
							<li>
								<div class="font-semibold">
									8. Completed Product or in Final Stages of Production:
								</div>
								If you wish to file for a refund, once the order has completed
								the production phase or is in the final phases of production
								and/or is received in our warehouse, LAAM will deduct 50% of
								your paid amount as cost of production, labor, and resources.
							</li>
							<li>
								<div class="font-semibold">
									Product is gone on Sale Price Afterwards:
								</div>
								LAAM does not offer price-match or price adjustment policy once
								the item has gone on sale after Order Confirmation/ Payment
								Confirmation.
							</li>
							<li>
								<div class="font-semibold">Active Chargeback:</div>
								Refund can’t be issued as long as a chargeback/dispute on the
								payment is active and the case is in progress.
							</li>
							<li>
								<div class="font-semibold">
									Pre-Booking & Out of stock Items:
								</div>
								Please be aware that we may be unable to deliver selected
								advance payment merchandise due to production problems or
								quality checks issues identified when we receive an order into
								stock. In these circumstances we will notify you and refund the
								advance payment to your debit/credit card within thirty days of
								being advised that merchandise has become unavailable.
							</li>
						</ul>
						<div class="my-4 font-semibold">For Order stuck in Customs</div>
						<div class="font-semibold">Refund Disclaimers:</div>
						<ul class="list-disc list-inside flex flex-col">
							<li>
								LAAM offers you the right either to take your refund in form of
								cash return or store credit that can be used for future
								purchase.
							</li>
							<li>
								Please note in case you have been catered with store credit for
								an order then you will not be considered eligible for refund for
								the same order.
							</li>
							<li>
								Once store credit has been issued it will not be
								reversed/exchanged with a refund.
							</li>
						</ul>
						<div class="mt-4 font-semibold">
							Refunds When Selling In Multiple Currencies:
						</div>
						Refunds are always issued to a customer in the currency that they
						used to pay for the order. When we issue refunds, the following
						rules apply:
						<ul class="list-disc list-inside flex flex-col">
							<li>
								Refunds use the same payment method that was used to pay for the
								order. We cannot issue a refund to a different card or bank
								account.
							</li>
							<li>
								Refunds are issued in the same currency that the customer used
								to pay for the order. If the customer paid in Euros, then they
								receive a refund in Euros.
							</li>
							<li>
								If the currency needs to be converted for the refund, then the
								current currency rate, and not the currency rate at the time of
								the order, is used in the conversion. You're charged a currency
								conversion fee when the payment is converted, but not when a
								refund is issued. Currency conversion fees and credit card fees
								aren't returned to you when you issue a refund.
							</li>
							<li>
								If we use a manual conversion rate, then refunds are calculated
								using the currency rate at the time of the order and not the
								current currency rate.
							</li>
						</ul>
						<div class="my-4">Timeline for Refund: 14 days to 45 days</div>
					</div>
					<div>
						<table
							class="max-h-[429px] w-full overflow-y-auto overscroll-contain rounded border border-borderColor"
						>
							<thead>
								<tr
									class="bg-borderColor text-left text-sm font-medium text-dark"
								>
									<th class="px-4 py-2">Payment methods</th>
									<th class="px-4 py-2">Refund option</th>
									<th class="px-4 py-2">Refund time</th>
								</tr>
							</thead>
							<tbody class="max-h-[429px">
								<tr class="border-b border-borderColor text-sm text-[#868686]">
									<td class="px-4 py-2">Debit card / credit card</td>
									<td class="px-4 py-2">Debit card / credit card</td>
									<td class="px-4 py-2">7-14 business days</td>
								</tr>
								<tr class="border-b border-borderColor text-sm text-[#868686]">
									<td class="px-4 py-2">Direct bank transfer</td>
									<td class="px-4 py-2">
										Direct bank transfer Store Credit Voucher
									</td>
									<td class="px-4 py-2">7-14 business days 2 day</td>
								</tr>
								<tr class="border-b border-borderColor text-sm text-[#868686]">
									<td class="px-4 py-2">COD</td>
									<td class="px-4 py-2">Voucher/ Store credit</td>
									<td class="px-4 py-2">2 business days</td>
								</tr>
								<tr class="border-b border-borderColor text-sm text-[#868686]">
									<td class="px-4 py-2">
										Quick Pay / Zelle / Western union / Moneygram
									</td>
									<td class="px-4 py-2">Chase Bank/ Zelle</td>
									<td class="px-4 py-2">5 business days</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div>
						<div class="text-dark">COD Orders</div>
						<div>
							Orders paid on cash on delivery are only refunded if by any chance
							the customer mistakenly paid the amount twice, once via bank
							deposit and once as cash while receiving the parcel, for such
							cases you need to report within an allotted time mentioned in our
							policy. Our case manager will review your request if it gets
							approved you will be notified and your refund will be processed
							within 10 business days.
						</div>
						PAID orders<br />

						<div>
							Paid orders are only refunded if reported within an allotted time
							mentioned in our policy and the customer provides proof of
							evidence and proof of payment for requesting a refund. Refund Case
							Manager will look at your case and will respond within 5 business
							days whether your request has been approved or rejected for
							refund. Refund will be approved based on the eligibility clauses
							and rejected on the basis of non-eligibility clauses.
						</div>
					</div>
					<div>
						<div class="text-dark">PARTIAL PAYMENT ORDERS</div>
						Incase of a Bridal order, any amount paid as ‘ advance payment’ for
						bridal/couture orders is non-refundable and non-transferrable. If
						you wish to file for a refund, once the order has completed the
						production phase or is in the final phases of production and/or is
						received in our warehouse, LAAM will deduct 50% of your paid amount
						as cost of production, labor, and resources has already incurred by
						the brand and LAAM has already paid that amount to the brand for
						your order to go into production.
					</div>
					<div>
						<div class="text-dark">PRE BOOKING ORDERS</div>
						You may from time to time be given the possibility of making an
						advance payment for certain items in which case you are able to make
						an advance purchase. This will ensure that you receive this item in
						priority once designer stock has been delivered to us. We will only
						take advance purchase orders for stock that has been scheduled for
						delivery by a designer supplier. Your rights regarding advance
						purchase are the same as those for any other purchase on our site.
						Refund is eligible to be issued if there are any delays in the
						delivery timeline from the designers end and the customer is timely
						informed of such delay and the customer has the right to exercise
						the change of mind option and request for a refund. Refunds will not
						be issued once the pre-booked item has been dispatched from the
						warehouse, shipped & delivered to the customer.
					</div>
					<div>
						<div class="text-dark">READY TO SHIP ORDERS</div>
						Ready to Ship orders are only refunded if reported within an
						allotted time mentioned in our policy and the customer provides
						proof of evidence and proof of payment for requesting a refund.
						Refund Case Manager will look at your case and will respond within 5
						business days whether your request has been approved or rejected for
						refund. Refund will be approved based on the eligibility clauses and
						rejected on the basis of non-eligibility clauses. Refunds will not
						be issued once the pre-booked item has been dispatched from the
						warehouse, shipped & delivered to the customer.
					</div>
					<div>
						<div class="text-dark">MADE TO ORDER/CUSTOMIZED ORDERS</div>
						You do not have a right to change your mind in respect of items
						which are made to order, bespoke or personalised. Incase of a Bridal
						order, any amount paid as ‘ advance payment’ for bridal/couture
						orders is non-refundable and non-transferrable. If you wish to file
						for a refund, once the order has completed the production phase or
						is in the final phases of production and/or is received in our
						warehouse, LAAM will deduct 50% of your paid amount as cost of
						production, labor, and resources has already incurred by the brand
						and LAAM has already paid that amount to the brand for your order to
						go into production.
					</div>
					<div>
						<div class="text-dark">OUT OF STOCK ORDERS</div>
						We will contact you in advance to tell you we will be suspending
						supply of the product, unless the problem is urgent or an emergency.
						You may contact us to end the contract for a product if we suspend
						it, or tell you we are going to suspend it and we will refund any
						sums you have paid in advance for the product in respect of the
						period after you end the contract.
						<div class="my-4"></div>
						If our supply of the products is delayed by an event outside our
						control then we will contact you as soon as possible to let you know
						and we will take steps to minimise the effect of the delay. Provided
						we do this we will not be liable for delays caused by the event, but
						if there is a risk of substantial delay you may contact us to end
						the contract and receive a refund for any products you have paid for
						but not received. Return Policy:
						<div class="mt-4">
							<div class="text-dark">How to file for a Return or Exchange?</div>
							To return or exchange a product please email us the order # with
							product name, original proof of purchase receipt and issue at
							{{ globalStore.store.meta_data?.email }} and our customer care
							agent will get in contact with you. Alternatively you can call us
							on {{ globalStore.store.meta_data?.phone }} from Monday to
							Saturday between 10:00 am - 6:00 pm (Pakistan Standard Time).
							Further, to proceed with the case, your purchase should be sent
							back to us within 14 days of receiving your order.
							<div class="mt-4">
								Visit : https://{{
									globalStore.store.domain
								}}/pages/refund-policy?contact_posted=true#contact_form
							</div>
						</div>
					</div>
					<div>
						<div class="text-dark">RETURN PRODURE</div>
						Returns of any order are entertained only if reported within 48
						hours of delivery, with original proof of purchase receipt sent to
						you via email at the time of purchase. To return any item follow
						below given steps:
						<br />Gather your items & take the picture of those items as proof
						to share with our customer care team. <br />
						Locate your receipt that is sent to you via email and contains the
						original order number. Order Confirmations & Receipts are
						automatically generated by our system and sent out to the email ID
						provided by the customer once the customer checks out and completes
						the order purchase process on our website.
						<br />Initiate a request for return at LAAM’s website by providing
						your order details that match with your name, email address, and
						phone number with your reason for return and proof of your claim for
						return such as pictures, videos, etc. Customer care team will look
						into your request, and will contact you if it finds your request
						genuine and eligible for return.<br />
						Reship it back to us by any courier services if the customer support
						team agrees with your case. After reviewing the nature of the
						complaint, we may offer you an exchange or store credit of less or
						equal value with a validity date of the credit. In case a return
						application is approved, it will only be for the price of the
						article(s), and will not cover the shipping and/or stitching cost.
						In case your claim for return is rejected by customer care, you’ll
						be notified within 5 business days.<br />
					</div>
					<div>
						Note: We are only responsible for returns once it reaches our
						warehouse.
						<br />
						Please note returns are catered only if returned products are sent
						through courier services. All successfully returned items can be
						exchanged for other merchandise or online store credit. In case of a
						product being faulty or wrongly delivered, LAAM.pk will follow the
						policy of repair following replacement and finally store credit if
						the item cannot be repaired or replaced. Timeline for Refund against
						a returned item : 14 days to 45 days
					</div>
					<div>
						<div class="text-dark">ELIGIBILITY CRITERIA FOR A RETURN</div>
						If your item arrived in defective / damaged or incorrect /
						incomplete condition, a refund against return will be issued.
						Incorrect product has been delivered. The product received is
						incomplete.
					</div>
					<div>
						<div class="text-dark">NON ELIGIBILITY CRITERIA FOR A RETURN</div>
						Return if the item is "No longer needed" or "Changed my mind" or "no
						technical reason but just want to cancel the order" or "you do not
						like the product after opening the package'* any other reasons
						listed apart from the ones listed in 'Eligibility'.
					</div>
					<div>
						>Any *Made to order* or *Customized orders* can not be returned. All
						'Made to orders' /Customized orders are shown to the customers
						before dispatching/shipping out the orders. This is done to ensure
						that customers get what they ordered. Only after the quality check
						and approval from the customers, products are shipped to them. Any
						orders where a customer has been given a special discount as a favor
						can not be returned. LAAM does not cater returns reported after the
						first 48 hours of delivery.
					</div>
					<div>
						If a product is returned to LAAM Warehouse in an inadequate
						condition or if the product is tempered, damaged or the care
						instructions have been violated, we reserve the right to send it
						back to you or refuse to issue the refund.
					</div>
					<div>
						<div class="text-dark">EXCHANGE PROCEDURE</div>
						At LAAM, we are committed to ensuring the highest level of service.
						Whilst we hope you are delighted with your order, we do not offer
						any return of exchange under normal conditions. We only offer
						Conditional Exchange if you face quality issues with the product.
						<br />LAAM urges you to file for exchange of product if reported
						within 48 hours of order confirmation/ delivery. To file an
						application for exchange kindly follow below given steps:<br />
						Contact our customer care via WhatsApp number, or Email Id provided
						on the website. Share the link of the outfit that you want to
						exchange your previous outfit with our customer care team.
						<br />Confirm your exchange by providing desired size guidelines or
						customizations. <br />You will receive exchange confirmation within
						the next 24 to 48 hours.
					</div>
					<div>
						<div class="text=dark">NON ELIGIBILITY CRITERIA</div>
						for an EXCHANGE LAAM holds the right to not amend/ edit orders after
						48 hours of purchase confirmation. Items ordered on sale are not
						entertained for exchange or return.
					</div>
					<div>
						PLEASE NOTE LAAM will not cater exchange for any product that has
						been washed, dried, damaged or stained in any way. This includes
						items that have been dried or damaged in the dryer. LAAM is not
						responsible for bleeding, damage of print or fading of colors due to
						washing and ironing.
					</div>
					<div>
						<div class="text-dark">FOR ASSISTANCE</div>
						Whatsapp
						<a
							:href="`${globalStore.store.meta_data?.whatsapp}`"
							target="_blank"
							style="color: #868686"
						>
							<span class="font-medium">{{
								globalStore.store.meta_data?.phone
							}}</span>
						</a>
						<br />EMAIL : {{ globalStore.store.meta_data?.email }} <br />Privacy
						And Cookie Policy:<br />
						LAAM Technologies Inc ("We") are committed to protecting and
						respecting your privacy. This policy (together with our terms of use
						and any other documents referred to on it) sets out the basis on
						which any personal data we collect from you, or that you provide to
						us, will be processed by us. Please read the following carefully to
						understand our views and practices regarding your personal data and
						how we will treat it. By visiting
						{{ globalStore.store.domain }} (the “site”) you are accepting and
						consenting to the practices described in this policy.
					</div>
					<div>
						<div class="text-dark">INFORMATION WE MAY COLLECT FROM YOU</div>
						We may collect and process the following data about you:
					</div>
					<div>
						Information you give us. You may give us information about you by
						filling in forms on our site www.studiobytcs.com or by corresponding
						with us by phone, e-mail or otherwise. This includes information you
						provide when you register to use our site, subscribe to our service,
						search for a product, place an order on our site, participate in
						discussion boards or other social media functions on our site, enter
						a competition, promotion or survey and when you report a problem
						with our site. The information you give us may include your name,
						address, e-mail address and phone number, financial and credit card
						information.
					</div>
					<div>
						Information we collect about you. With regard to each of your visits
						to our site we may automatically collect the following information:
						technical information, including the Internet protocol (IP) address
						used to connect your computer to the Internet, your login
						information, browser type and version, time zone setting, browser
						plug-in types and versions, operating system and platform;
					</div>
					<div>
						Information about your visit, including the full Uniform Resource
						Locators (URL) clickstream to, through and from our site (including
						date and time); products you viewed or searched for; page response
						times, download errors, length of visits to certain pages, page
						interaction information (such as scrolling, clicks, and
						mouse-overs), and methods used to browse away from the page and any
						phone number used to call our customer service number.
					</div>
					<div>
						Information we receive from other sources. We may receive
						information about you if you use any of the other websites we
						operate or the other services we provide. We also work closely with
						third parties (including, for example, business partners,
						sub-contractors in technical, payment and delivery services,
						advertising networks, analytics providers, search information
						providers, credit reference agencies) and may receive information
						about you from them.
					</div>
					<div>
						<div class="text-dark">COOKIES POLICY</div>
						Our website uses cookies to distinguish you from other users of our
						website. A cookie is a small amount of data, which may include a
						unique identifier. This helps us to provide you with a good
						experience when you browse our website and also allows us to improve
						our site. For detailed information on the cookies we use, the
						purposes for which we use them and how to opt out of cookies, see
						https://www.shopify.com/legal/privacy.
					</div>
					<div>
						<div class="text-dark">USES MADE OF THE INFORMATION</div>
						We use information held about you in the following ways: Information
						you give to us. We will use this information; to provide you with
						information about other goods and services we offer that are similar
						to those that you have already purchased or enquired about; to
						provide you, or permit selected third parties to provide you, with
						information about goods or services we feel may interest you. If you
						are an existing customer, we will only contact you by electronic
						means (e-mail or SMS) with information about goods and services
						similar to those which were the subject of a previous sale or
						negotiations of a sale to you. If you are a new customer, and where
						we permit selected third parties to use your data, we (or they) will
						contact you by electronic means only if you have consented to this.
						If you do not want us to use your data in this way, or to pass your
						details on to third parties for marketing purposes, please tick the
						relevant box situated on the form on which we collect your data; to
						notify you about changes to our service; to ensure that content from
						our site is presented in the most effective manner for you and for
						your computer.
						<br />
						Information we collect about you. We will use this information:
						<br />to administer our site and for internal operations, including
						troubleshooting, data analysis, testing, research, statistical and
						survey purposes; to improve our site to ensure that content is
						presented in the most effective manner for you and for your
						computer; <br />to allow you to participate in interactive features
						of our service, when you choose to do so; as part of our efforts to
						keep our site safe and secure; <br />to measure or understand the
						effectiveness of advertising we serve to you and others, and to
						deliver relevant advertising to you; to make suggestions and
						recommendations to you and other users of our site about goods or
						services that may interest you or them.<br />
						Information we receive from other sources. We may combine this
						information with information you give to us and information we
						collect about you. We may use this information and the combined
						information for the purposes set out above (depending on the types
						of information we receive).
					</div>
					<div>
						<div class="text-dark">DISCLOSURE OF YOUR INFORMATION</div>
						We may share your personal information with any member of our group,
						which means our subsidiaries, our ultimate holding company and its
						subsidiaries.
					</div>
					<div>
						We may share your information with selected third parties including:
						<br />
						Business partners, suppliers and subcontractors for the running of
						our website and performance of any contract between them or you.
					</div>
					<div>
						Advertisers and advertising networks that require the data to select
						and serve relevant adverts to you and others. We do not disclose
						information about identifiable individuals to our advertisers, but
						we may provide them with aggregate information about our users (for
						example, we may inform them that 500 women aged under 30 have
						clicked on their advertisement on any given day). We may also use
						such aggregate information to help advertisers reach the kind of
						audience they want to target (for example, women in SW1). We may
						make use of the personal data we have collected from you to enable
						us to comply with our advertisers' wishes by displaying their
						advertisement to that target audience.
					</div>
					<div>
						Analytics and search engine providers that assist us in the
						improvement and optimisation of our site.
					</div>
					<div>
						Credit reference agencies for the purpose of assessing your credit
						score where this is a condition of us entering into a contract with
						you. We may disclose your personal information to third parties:
						<br />
						In the event that we sell or buy any business or assets, in which
						case we may disclose your personal data to the prospective seller or
						buyer of such business or assets.<br />
						If we or substantially all of our assets are acquired by a third
						party, in which case personal data held by us about our customers
						will be one of the transferred assets. <br />
						If we are under a duty to disclose or share your personal data in
						order to comply with any legal obligation, or in order to enforce or
						apply our terms of use, terms and conditions of supply and other
						agreements; or to protect our rights, property, or safety or the
						rights, property, or safety of our customers and/or others. This
						includes exchanging information with other companies and
						organisations for the purposes of fraud protection and credit risk
						reduction.
					</div>
					<div>
						<div class="text-dark">WHERE WE STORE YOUR PERSONAL DATA</div>
						The data that we collect from you may be transferred to, and stored
						at, a destination outside the USA. It may also be processed by staff
						operating outside the USA who work for us or for one of our
						suppliers. Such staff may be engaged in, among other things, the
						fulfilment of your order, the processing of your payment details and
						the provision of support services. By submitting your personal data,
						you agree to this transfer, storing or processing. We will take all
						steps reasonably necessary to ensure that your data is treated
						securely and in accordance with this privacy policy.
						<br />
						All information you provide to us is stored on our secure servers.
						Any payment transactions will be encrypted using SSL technology.
						Where we have given you (or where you have chosen) a password which
						enables you to access certain parts of our site, you are responsible
						for keeping this password confidential. We ask you not to share a
						password with anyone.
						<br />
						Unfortunately, the transmission of information via the internet is
						not completely secure. Although we will do our best to protect your
						personal data, we cannot guarantee the security of your data
						transmitted to our site; any transmission is at your own risk. Once
						we have received your information, we will use strict procedures and
						security features to try to prevent unauthorised access.
					</div>
					<div>
						<div class="text-dark">YOUR RIGHTS</div>
						You have the right to ask us not to process your personal data for
						marketing purposes. We will usually inform you (before collecting
						your data) if we intend to use your data for such purposes or if we
						intend to disclose your information to any third party for such
						purposes. You can exercise your right to prevent such processing by
						checking certain boxes on the forms we use to collect your data.
						<br />Our site may, from time to time, contain links to and from the
						websites of our partner networks, advertisers and affiliates. If you
						follow a link to any of these websites, please note that these
						websites have their own privacy policies and that we do not accept
						any responsibility or liability for these policies. Please check
						these policies before you submit any personal data to these
						websites.
					</div>
					<div>
						<div class="text-dark">ACCESS TO INFORMATION</div>
						The Act gives you the right to access information held about you.
						Your right of access can be exercised in accordance with the Act.
					</div>
					<div>
						<div class="text-dark">CHANGES TO OUR PRIVACY POLICY</div>
						Any changes we may make to our privacy policy in the future will be
						posted on this page and, where appropriate, notified to you by
						e-mail. Please check back frequently to see any updates or changes
						to our privacy policy.<br />
						FOR ASSISTANCE Whatsapp:
						<a
							:href="`${globalStore.store.meta_data?.whatsapp}`"
							target="_blank"
							style="color: #868686"
						>
							<span class="font-medium">{{ globalStore.store.meta_data?.phone }}
							</span>
						</a>
						<br />EMAIL : {{ globalStore.store.meta_data?.email }}
					</div>
					<div class="text-black">
						{{ globalStore.store.name }} Disclaimers:
					</div>
					<div>
						<div class="text-black">COLOR VARIATION DISCLAIMER</div>
						Products may vary slightly from their pictures. The images of the
						products on our website are for illustrative purposes only. Although
						we have made every effort to display the colours accurately, we
						cannot guarantee that a device's display of the colours accurately
						reflects the colour of the products. Your product may vary slightly
						from those images.
					</div>
					<div>
						Due to variations in monitor settings, LAAM assumes no
						responsibility regarding color matches of the product, either within
						an order or in a reorder. Please directly contact us at
						{{ globalStore.store.meta_data?.email }} if you have any queries
						about the exact color/shade. <br />Note that like-named colors used
						by various brands (i.e. Navy, Navy Blue, Dark Navy, etc.) are not
						necessarily an exact shade match to each other or to any of the
						Brands Thread Guide options.
					</div>
					<div>
						Due to the nature of apparel manufacturing from various brands ends,
						LAAM does not guarantee the consistency, color, texture or
						appearance of the actual apparel. The colors displayed are the
						closest possible variant but hues & tones may slightly differ due to
						monitor/screen settings.Pictures of outfits and different products
						given on the website are taken during shoot, under mixture of
						natural and artificial lighting. Such pictures then go through
						editing. These pictures are for advertising and reference purposes
						only and actual products may vary from the picture.
					</div>
					<div>
						Please note that different types of fabrics absorb the same color
						differently and thus reflect different shades of the same color. We
						try to present the product in the picture closest to reality but
						actual products can vary and we hold no responsibility for that.
						Please read product description clearly before purchase
					</div>
					<div>
						<div class="text-dark">SIZE DISCLAIMER</div>
						We make every effort to give you accurate manufacturer reported
						sizing information for the products in our size guides available
						alongside each product on the product page . However, please note
						that due to the nature of the manufacturing process the product
						sizing may vary slightly. LAAM is not responsible for sizing
						variations in the manufacturing process.
					</div>
					<div>
						<div class="text-dark">HOW TO GET THE RIGHT FIT</div>
						To measure your clothing size follow these instructions:
						<LazyImage
							src="https://cdn.shopify.com/s/files/1/2337/7003/files/GmaWqAW_480x480.jpg?v=1624091489"
							alt="image"
						/>
					</div>
					<div>
						<div class="text-dark">CHOOSE THE RIGHT SIZE</div>
						That's easier said than done. Because, what do you do if you are in
						between two sizes?Well, that's up to you. Do you like a tight fit?
						Go for the smaller size. Loving a loose fit? Go for the larger
						size.Top fit: If your body measurements for bust and waist result in
						two different suggested sizes, order the size from your bust
						measurement.
					</div>
					<div>
						<div class="text-dark">PRODUCT CARE INSTRUCTIONS</div>
						LAAM encourages its customers to ensure proper care procedures of
						the purchased product which will help in optimizing the lifespan of
						the high performance product that you have invested your money in as
						a customer. Each brand has their own garment/product care policy and
						it will either be attached on the product itself, printed on the
						packaging or available on their website. Following these simple
						instructions will maximize the life of the product. Since LAAM are
						not the manufacturers of any products available on our website,
						therefore we hold no responsibilities or cater to any issues of
						product mishandling or violation of product care instructions and
						customers will not be issued any refund, cancellation of order, or
						exchange if any of the care instructions have been violated.
						<br />
						Following points will be considered as violation of care
						instructions:
						<br />
						If the outfit has been shrunk. <br />
						If the outfit has been washed/ dry clean. <br />
						If an outfit has been stitched even in cases of missing pieces.
						<br />
						If the outfit has been worn or tried on. <br />
						If the item has been subjected to bleach/chemical for stain
						removing.
						<br />
						A list of care instructions/symbols are provided by the designer and
						if any of those instructions are also not followed properly.
						<LazyImage
							src="https://cdn.shopify.com/s/files/1/2337/7003/files/Laundry_Care_Symbols-01-1024x683_480x480.jpg?v=1624091522"
							alt="img"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
